import { Briefcase, FileText, ExternalLink } from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";

/**
 * Dashboard widget surfacing budget categories with active invoices —
 * effectively contracts in progress. Renders a per-contract progress bar
 * (paid-or-allocated / contract value) with a list of contributing invoices.
 *
 * v1 contract detection heuristic:
 *  - category has at least 1 invoice allocation, AND
 *  - planned total > 0 (used as contract value when contractValue isn't set)
 *
 * Hidden entirely when no contracts qualify so the dashboard stays clean
 * for projects without any invoice activity yet.
 */

const catTotal = (c) => (c.estimatedMat || 0) + (c.estimatedLabor || 0);

export default function ContractsInProgress({ budget, invoices }) {
  if (!invoices?.length) return null;

  // Group invoice allocations by budget category id
  const byCategory = new Map();
  for (const inv of invoices) {
    for (const a of inv.allocations || []) {
      const list = byCategory.get(a.budgetCategoryId) || [];
      list.push({ ...a, vendor: inv.vendor, status: inv.status, pdfDownloadURL: inv.pdfDownloadURL, dateIssued: inv.dateIssued });
      byCategory.set(a.budgetCategoryId, list);
    }
  }

  // Build the contract rows: one per category that has invoices
  const rows = [];
  for (const c of budget || []) {
    const allocs = byCategory.get(c.id);
    if (!allocs?.length) continue;
    const planned = catTotal(c);
    const contractValue = c.contractValue ?? planned;
    if (contractValue <= 0) continue;
    const billed = allocs.reduce((s, a) => s + (a.amount || 0), 0);
    const paid = allocs
      .filter((a) => a.status === "paid")
      .reduce((s, a) => s + (a.amount || 0), 0);
    rows.push({
      categoryId: c.id,
      categoryName: c.name,
      contractValue,
      billed,
      paid,
      allocs,
    });
  }

  if (rows.length === 0) return null;

  return (
    <InfoBentoCard
      cornerGlow
      frostedGradient
      icon={<Briefcase className="w-4 h-4" />}
      accent="purple"
      title="Contracts in progress"
      subtitle={`${rows.length} active contract${rows.length === 1 ? "" : "s"}`}
      className="mb-6"
    >
      <div className="space-y-4 mt-3">
        {rows.map((row) => {
          const billedPct = Math.min(100, Math.round((row.billed / row.contractValue) * 100));
          const paidPct = Math.min(100, Math.round((row.paid / row.contractValue) * 100));
          return (
            <div key={row.categoryId}>
              <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                <span className="text-xs font-semibold" style={{ color: "var(--text-heading)" }}>
                  {row.categoryName}
                </span>
                <span className="text-[11px] font-mono" style={{ color: "var(--text-secondary)" }}>
                  ${row.billed.toLocaleString()}
                  <span style={{ color: "var(--text-muted)" }}> / ${row.contractValue.toLocaleString()}</span>
                  <span className="ml-2 text-emerald-400">{paidPct}% paid</span>
                </span>
              </div>
              {/* Stacked bar: paid (emerald), billed-but-unpaid (amber), remainder (track) */}
              <div className="w-full rounded-full h-2 overflow-hidden relative" style={{ background: "var(--bg-progress-track)" }}>
                <div className="absolute inset-y-0 left-0 bg-amber-500/50" style={{ width: `${billedPct}%` }} />
                <div className="absolute inset-y-0 left-0 bg-emerald-500/70" style={{ width: `${paidPct}%` }} />
              </div>
              <div className="mt-1.5 space-y-0.5">
                {row.allocs.map((a, i) => (
                  <a
                    key={i}
                    href={a.pdfDownloadURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[10px] px-1 py-0.5 rounded hover:bg-white/[0.04]"
                  >
                    <FileText className="w-2.5 h-2.5" style={{ color: "var(--text-muted)" }} />
                    <span className="flex-1 truncate" style={{ color: "var(--text-secondary)" }}>
                      {a.vendor || "—"} <span style={{ color: "var(--text-muted)" }}>· {a.status}</span>
                    </span>
                    <span className="font-mono" style={{ color: "var(--text-body)" }}>
                      ${a.amount.toLocaleString()}
                    </span>
                    <ExternalLink className="w-2.5 h-2.5" style={{ color: "var(--text-muted)" }} />
                  </a>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </InfoBentoCard>
  );
}
